import React from 'react';
import { generatePath } from 'react-router-dom';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import classNames from 'classnames';
import RouterLink from '@/components/common/RouterLink';
import ConnectionStatus from '@/constants/ConnectionStatus';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import translate from '@/lib/intl/translate';
import { type Integration } from '@/types/Integration';

export type IntegrationCardProps = {
  integration: Integration;
  isOrgAdmin: boolean;
};

function IntegrationCard({
  integration,
  isOrgAdmin = false,
}: IntegrationCardProps) {
  const {
    shortDescription,
    id,
    logo: Logo,
    name,
    isPersonal,
    status,
  } = integration;

  let cta = translate('INTEGRATIONS_CTA_CONNECT');

  switch (status) {
    case ConnectionStatus.PENDING:
      cta = translate('INTEGRATIONS_CTA_ACTION_REQUIRED');
      break;
    default:
      cta = translate('INTEGRATIONS_CTA_CONNECT');
      break;
  }
  const needsActionClassName =
    'flex items-center rounded-lg bg-success-light p-3 text-tertiary-dark opacity-80 type-body2';

  const isPending = status === ConnectionStatus.PENDING;
  const isConnected = status === ConnectionStatus.CONNECTED;

  const content = (
    <div className="flex-col p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {typeof Logo === 'string' ? (
            <img className="h-14 w-14 rounded" src={Logo} alt={name} />
          ) : (
            <Logo className="h-14 w-14" />
          )}
          <h4 className="ml-3 text-text-primary type-h3">{name}</h4>
        </div>
        {isConnected ? (
          <div className={needsActionClassName}>
            <FiberManualRecordIcon
              fontSize="small"
              className="mr-2 text-tertiary-dark"
            />
            {translate('INTEGRATIONS_CONNECTED')}
          </div>
        ) : (
          <span
            className={classNames({
              'text-tertiary-main': !isConnected,
              [`bg-warning-light text-[#5D3800] ${needsActionClassName}`]:
                isPending,
            })}
          >
            {cta}
          </span>
        )}
      </div>
      <p className="pt-5 text-grey-900 type-body3">{shortDescription}</p>
    </div>
  );

  const classes = classNames(
    'flex w-full flex-col rounded-lg border border-grey-300 hover:shadow-lg',
    { 'pointer-events-none opacity-50': !isOrgAdmin && !isPersonal },
  );

  if (isOrgAdmin || isPersonal) {
    return (
      <RouterLink
        className={classes}
        to={generatePath(AuthRoutes.INTEGRATION, {
          integrationId: id,
        })}
      >
        {content}
      </RouterLink>
    );
  }

  return <div className={classes}>{content}</div>;
}

export default IntegrationCard;
